/**
 * Bereitet ein Ergebnis von api/StatisticEvaluation/Evaluate fuer die Anzeige
 * auf (CARE-814). Der Server liefert die feinste Gruppierung als flache
 * Zeilen; ob daraus eine einfache Tabelle oder eine Kreuztabelle wird, ist
 * reine Darstellung und wird hier entschieden.
 */

export const NO_VALUE = '–';
export const SUPPRESSED_VALUE = '< 3';
export const MISSING_DIMENSION_VALUE = 'ohne Angabe';

const NO_ROW_DIMENSION_HEADER = 'Gruppe';
const ALL_CASES_LABEL = 'Alle Fälle';

function labelFor(entries, key) {
  const entry = (entries || []).find((item) => item.Key === key);
  return entry && entry.Label ? entry.Label : key;
}

function measureFor(catalog, key) {
  return ((catalog && catalog.Measures) || []).find((item) => item.Key === key);
}

function measureUnit(catalog, key) {
  const measure = measureFor(catalog, key);
  return measure ? measure.Unit : null;
}

function measureHeader(catalog, key) {
  const label = labelFor(catalog && catalog.Measures, key);
  const unit = measureUnit(catalog, key);
  return unit ? `${label} (${unit})` : label;
}

export function formatMeasureValue(value, unit) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return NO_VALUE;
  }

  const fractionDigits = unit ? 1 : 0;
  const formatted = Number(value).toLocaleString('de-DE', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return unit === '%' ? `${formatted} %` : formatted;
}

function formatMeasureValues(measureKeys, values, suppressed, catalog) {
  return measureKeys.map((key, index) => {
    if (suppressed) {
      return SUPPRESSED_VALUE;
    }
    const value = Array.isArray(values) ? values[index] : null;
    return formatMeasureValue(value, measureUnit(catalog, key));
  });
}

function dimensionValues(row, count) {
  const values = (row && row.DimensionValues) || [];
  const result = [];
  for (let index = 0; index < count; index += 1) {
    const value = values[index];
    result.push(
      value === null || value === undefined || value === ''
        ? MISSING_DIMENSION_VALUE
        : String(value),
    );
  }
  return result;
}

// Nur fuer Werte, deren Reihenfolge die Zeilen nicht festlegen; entspricht
// dem Vergleich des Servers ohne dessen eigene Sortierschluessel.
function fallbackCompare(left, right) {
  const leftMissing = left === MISSING_DIMENSION_VALUE;
  const rightMissing = right === MISSING_DIMENSION_VALUE;
  if (leftMissing !== rightMissing) {
    return leftMissing ? 1 : -1;
  }
  return left.localeCompare(right, 'de', { numeric: true });
}

/**
 * Rangfolge der Werte einer Dimension, abgeleitet aus der Serverreihenfolge.
 * Der Server sortiert die Zeilen lexikografisch ueber alle Dimensionen
 * (Zeit chronologisch, sonst alphabetisch, "ohne Angabe" zuletzt). Bei
 * gleichen vorderen Werten steigen die Werte dieser Dimension also an; die
 * Paare daraus werden topologisch sortiert. Das erste Auftreten reicht nicht:
 * Eine Zeilengruppe kann spaetere Werte vor frueheren enthalten.
 */
function dimensionRanks(valueRows, dimensionIndex) {
  const successors = new Map();
  const predecessorCount = new Map();
  const register = (value) => {
    if (!successors.has(value)) {
      successors.set(value, new Set());
      predecessorCount.set(value, 0);
    }
  };

  let previousPrefix = null;
  let previousValue = null;
  valueRows.forEach((values) => {
    const value = values[dimensionIndex];
    register(value);
    const prefix = JSON.stringify(values.slice(0, dimensionIndex));
    if (
      prefix === previousPrefix &&
      value !== previousValue &&
      !successors.get(previousValue).has(value)
    ) {
      successors.get(previousValue).add(value);
      predecessorCount.set(value, predecessorCount.get(value) + 1);
    }
    previousPrefix = prefix;
    previousValue = value;
  });

  const ranks = new Map();
  let ready = Array.from(predecessorCount.keys()).filter(
    (value) => predecessorCount.get(value) === 0,
  );
  while (ready.length > 0) {
    ready.sort(fallbackCompare);
    const value = ready.shift();
    ranks.set(value, ranks.size);
    successors.get(value).forEach((next) => {
      predecessorCount.set(next, predecessorCount.get(next) - 1);
      if (predecessorCount.get(next) === 0) {
        ready = ready.concat(next);
      }
    });
  }

  // Widerspruechliche Reihenfolge (unsortierte Antwort): Rest hinten anhaengen.
  Array.from(successors.keys())
    .filter((value) => !ranks.has(value))
    .sort(fallbackCompare)
    .forEach((value) => ranks.set(value, ranks.size));

  return ranks;
}

/**
 * Rangfolge nach OrderedDimensionValues des Servers. Fehlt die Liste (aelterer
 * Server) oder einer der vorkommenden Werte darin, werden die uebrigen Werte
 * in der abgeleiteten Reihenfolge angehaengt.
 */
function preferServerOrder(serverValues, derivedRanks) {
  if (!Array.isArray(serverValues)) {
    return derivedRanks;
  }

  const ranks = new Map();
  serverValues
    .filter((value) => derivedRanks.has(value) && !ranks.has(value))
    .forEach((value) => ranks.set(value, ranks.size));
  Array.from(derivedRanks.keys())
    .filter((value) => !ranks.has(value))
    .sort((left, right) => derivedRanks.get(left) - derivedRanks.get(right))
    .forEach((value) => ranks.set(value, ranks.size));
  return ranks;
}

function compareByRanks(left, right, ranksPerPosition) {
  for (let index = 0; index < ranksPerPosition.length; index += 1) {
    const ranks = ranksPerPosition[index];
    const difference = ranks.get(left[index]) - ranks.get(right[index]);
    if (difference !== 0) {
      return difference;
    }
  }
  return 0;
}

function buildTotal(result, measureKeys, catalog) {
  const total = result.Total || {};
  const suppressed = total.Suppressed === true;
  return {
    suppressed,
    cells: formatMeasureValues(
      measureKeys,
      total.MeasureValues,
      suppressed,
      catalog,
    ),
  };
}

function formatCaseCount(result) {
  const totalSuppressed = result.Total && result.Total.Suppressed === true;
  if (totalSuppressed || result.CaseCount === null) {
    return totalSuppressed ? SUPPRESSED_VALUE : NO_VALUE;
  }
  return formatMeasureValue(result.CaseCount, null);
}

function buildFlatRows(rows, dimensionCount, measureKeys, catalog) {
  if (dimensionCount === 0) {
    // Ohne Dimension ist die einzige Gruppe die Gesamtheit; sie steht schon in
    // der Gesamtzeile.
    return [];
  }

  return rows.map((row, index) => {
    const headers = dimensionValues(row, dimensionCount);
    return {
      key: `${index}-${headers.join('|')}`,
      headers,
      cells: formatMeasureValues(
        measureKeys,
        row.MeasureValues,
        row.Suppressed === true,
        catalog,
      ),
    };
  });
}

function buildCrossRows(
  rows,
  dimensionKeys,
  columnIndex,
  measureKeys,
  catalog,
  orderedDimensionValues,
) {
  const valueRows = rows.map((row) =>
    dimensionValues(row, dimensionKeys.length),
  );
  const serverOrder = Array.isArray(orderedDimensionValues)
    ? orderedDimensionValues
    : [];
  const ranks = dimensionKeys.map((_key, index) =>
    preferServerOrder(serverOrder[index], dimensionRanks(valueRows, index)),
  );
  const rowRanks = ranks.filter((_ranks, index) => index !== columnIndex);

  const groups = [];
  const groupByKey = {};

  rows.forEach((row, rowIndex) => {
    const values = valueRows[rowIndex];
    const columnValue = values[columnIndex];
    const headers = values.filter((_value, index) => index !== columnIndex);
    const groupKey = JSON.stringify(headers);

    if (!groupByKey[groupKey]) {
      groupByKey[groupKey] = { headers, cellsByColumn: {} };
      groups.push(groupByKey[groupKey]);
    }

    groupByKey[groupKey].cellsByColumn[columnValue] = formatMeasureValues(
      measureKeys,
      row.MeasureValues,
      row.Suppressed === true,
      catalog,
    );
  });

  groups.sort((left, right) =>
    compareByRanks(left.headers, right.headers, rowRanks),
  );
  const columnRanks = ranks[columnIndex];
  const columnValues = Array.from(columnRanks.keys()).sort(
    (left, right) => columnRanks.get(left) - columnRanks.get(right),
  );
  const emptyCells = measureKeys.map(() => '');

  return {
    columnValues,
    rows: groups.map((group, index) => ({
      key: `${index}-${group.headers.join('|')}`,
      headers: group.headers.length > 0 ? group.headers : [ALL_CASES_LABEL],
      cells: columnValues.reduce(
        (cells, value) =>
          cells.concat(group.cellsByColumn[value] || emptyCells),
        [],
      ),
    })),
  };
}

export function buildStatisticTable(result, catalog, columnDimensionKey) {
  const safeResult = result || {};
  const dimensionKeys = safeResult.DimensionKeys || [];
  const measureKeys = safeResult.MeasureKeys || [];
  const rows = (safeResult.Rows || []).filter(Boolean);
  const dimensions = (catalog && catalog.Dimensions) || [];

  const columnIndex = columnDimensionKey
    ? dimensionKeys.indexOf(columnDimensionKey)
    : -1;
  const isCrossTable = columnIndex >= 0;

  const rowDimensionKeys = dimensionKeys.filter(
    (_key, index) => index !== columnIndex,
  );
  const rowHeaders = rowDimensionKeys.map((key) => labelFor(dimensions, key));

  const table = {
    isCrossTable,
    rowHeaders: rowHeaders.length > 0 ? rowHeaders : [NO_ROW_DIMENSION_HEADER],
    measureHeaders: measureKeys.map((key) => measureHeader(catalog, key)),
    columnHeader: null,
    columnValues: [],
    rows: [],
    total: buildTotal(safeResult, measureKeys, catalog),
    caseCount: formatCaseCount(safeResult),
    hasSuppressed:
      (safeResult.Total && safeResult.Total.Suppressed === true) ||
      rows.some((row) => row && row.Suppressed === true),
    multiValueDimensions: dimensions
      .filter((item) => item.MultiValue && dimensionKeys.includes(item.Key))
      .map((item) => item.Label || item.Key),
  };

  if (isCrossTable) {
    const cross = buildCrossRows(
      rows,
      dimensionKeys,
      columnIndex,
      measureKeys,
      catalog,
      safeResult.OrderedDimensionValues,
    );
    table.columnHeader = labelFor(dimensions, columnDimensionKey);
    table.columnValues = cross.columnValues;
    table.rows = cross.rows;
  } else {
    table.rows = buildFlatRows(
      rows,
      dimensionKeys.length,
      measureKeys,
      catalog,
    );
  }

  return table;
}
